import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer, disposeOnUnmount } from 'mobx-react';
import { FULFILLED } from 'mobx-utils';
import c from 'classnames';
import { AccentButton } from '@lyearn-team/frontend-components';
import { reaction } from 'mobx';
import { attachTrack, detachTrack } from 'components/LiveClass/utils/attachDetachTrack';
import removeAllMediaElements from 'components/LiveClass/utils/removeAllMediaElements';
import UserMediaStore from 'components/LiveClass/model/store/UserMediaStore';
import * as s from './styles';

@observer
class UserMedia extends Component {
  constructor(props) {
    super(props);

    this.rootNodeRef = React.createRef();
    this.videoNodeRef = React.createRef();
    this.screenNodeRef = React.createRef();
  }

  componentDidMount() {
    this.attachMedia();

    disposeOnUnmount(this, [
      reaction(
        () => {
          const { mediaStore } = this.props;
          const { isVideoEnabled, isScreenShareEnabled, isAudioEnabled } = mediaStore;
          return { isVideoEnabled, isScreenShareEnabled, isAudioEnabled };
        },
        this.attachMedia,
        {
          delay: 300,
          fireImmediately: true
        }
      )
    ]);
  }

  attachMedia = () => {
    const { mediaStore } = this.props;
    if (!this.rootNodeRef.current) {
      return;
    }
    if (!mediaStore) {
      removeAllMediaElements(this.rootNodeRef.current);
      return;
    }

    const { showVideo, showAudio, showScreenShare } = this.props;

    if (showVideo) {
      const { videoPublication, isVideoEnabled } = mediaStore;
      if (videoPublication) {
        if (!isVideoEnabled) {
          detachTrack(videoPublication.track);
        } else {
          attachTrack(videoPublication.track, this.videoNodeRef.current);
        }
      }
    }

    if (showScreenShare) {
      const { screenSharePublication, isScreenShareEnabled } = mediaStore;
      if (screenSharePublication) {
        if (!isScreenShareEnabled) {
          detachTrack(screenSharePublication.track);
        } else {
          attachTrack(screenSharePublication.track, this.screenNodeRef.current);
        }
      }
    }

    if (showAudio) {
      const { audioPublication, isAudioEnabled } = mediaStore;
      if (audioPublication) {
        if (!isAudioEnabled) {
          detachTrack(audioPublication.track);
        } else {
          attachTrack(audioPublication.track, this.videoNodeRef.current);
        }
      }
    }
  };

  renderUserProfile = () => {
    const { mediaStore, isMobile } = this.props;
    const { isVideoEnabled } = mediaStore;
    if (isVideoEnabled) {
      return null;
    }

    const userPromise = mediaStore.user;
    const user = userPromise.state === FULFILLED ? userPromise.value : {};
    const { picture, name } = user;
    return (
      <div className={s.profilePictureContainer(isMobile)}>
        {picture && <img src={picture} alt={name} className={s.profilePicture} />}
      </div>
    );
  };

  renderName = (suffix) => {
    const { showName, mediaStore } = this.props;
    if (!showName) {
      return null;
    }

    const userPromise = mediaStore.user;
    const name = userPromise.state === FULFILLED ? userPromise.value.name : '';
    return (
      <div className={s.userName}>
        {name}
        {suffix}
      </div>
    );
  };

  renderFullScreenButton = (isFullScreen, toggleFullScreen) => {
    const { showFullScreen } = this.props;
    if (!showFullScreen) {
      return null;
    }

    const icon = isFullScreen ? 'backToNormal' : 'fullScreen';

    return (
      <AccentButton
        icon={{ type: icon }}
        className={s.fullScreenButton}
        onClick={toggleFullScreen}
      />
    );
  };

  renderScreenShare = () => {
    const { mediaStore, showScreenShare, isMobile } = this.props;
    const {
      isScreenShareEnabled,
      isScreenShareFullScreen,
      toggleScreenShareFullScreen
    } = mediaStore;
    if (!showScreenShare || !isScreenShareEnabled) {
      return null;
    }

    return (
      <div
        ref={this.screenNodeRef}
        className={c(s.screenContainer, { [s.mediaFullScreen(isMobile)]: isScreenShareFullScreen })}
      >
        {this.renderName("'s Screen")}
        {this.renderFullScreenButton(isScreenShareFullScreen, toggleScreenShareFullScreen)}
      </div>
    );
  };

  renderVideo = () => {
    const { mediaStore, isMobile } = this.props;
    const { isVideoFullScreen, toggleVideoFullScreen } = mediaStore;

    return (
      <div
        ref={this.videoNodeRef}
        className={c(s.videoContainer, {
          [s.mediaFullScreen(isMobile)]: isVideoFullScreen
        })}
      >
        {this.renderName()}
        {this.renderUserProfile()}
        {this.renderFullScreenButton(isVideoFullScreen, toggleVideoFullScreen)}
      </div>
    );
  };

  render() {
    const { mediaStore, className } = this.props;

    if (!mediaStore) {
      return null;
    }

    return (
      <div ref={this.rootNodeRef} className={c(s.root, className)}>
        {this.renderVideo()}
        {this.renderScreenShare()}
      </div>
    );
  }
}

UserMedia.defaultProps = {
  className: '',
  showVideo: true,
  showAudio: true,
  showScreenShare: true,
  showFullScreen: true,
  showName: true
};

UserMedia.propTypes = {
  className: PropTypes.string,
  mediaStore: PropTypes.instanceOf(UserMediaStore).isRequired,
  showVideo: PropTypes.bool,
  showAudio: PropTypes.bool,
  showScreenShare: PropTypes.bool,
  showFullScreen: PropTypes.bool,
  showName: PropTypes.bool
};

export default UserMedia;
